import {
    type CheckoutFieldName,
    type FieldErrorCode
} from "@/app/(site)/_components/Atoms/Inputs/errors/fieldErrorCodes"
import { t } from "@/translations"

const PREFIX = "checkout.information_for_billing_and_shipping.error_messages."

/**
 * Exhaustive by type: a new `FieldErrorCode` breaks the build here rather than reaching a
 * customer as a raw key path, which is what `t()` returns for a key it cannot find.
 */
export const FIELD_ERROR_MESSAGE_KEYS: Record<FieldErrorCode, string> = {
    value_missing: `${PREFIX}value_missing`,
    value_invalid: `${PREFIX}value_invalid`,
    value_too_long: `${PREFIX}value_too_long`,
    email_invalid: `${PREFIX}email_invalid`,
    phone_invalid: `${PREFIX}phone_number_invalid`,
    address_too_long: `${PREFIX}combined_address_and_apartment_house_number_length_cannot_exceed_35_characters`,
    po_box_not_allowed: `${PREFIX}po_box_not_allowed`,
    special_characters_not_allowed: `${PREFIX}special_characters_not_allowed`,
    zip_code_format_invalid: `${PREFIX}us_zip_code_validation_message`,
    zip_code_state_mismatch: `${PREFIX}zip_code_not_valid_for_state`,
    pfas_restricted: "checkout.pfas.restricted_product_message_form",
    address_not_verified: `${PREFIX}address_not_verified`
}

/** `value_missing` is the one code worded after the field rather than the failure. */
export const REQUIRED_MESSAGE_KEYS: Record<CheckoutFieldName, string> = {
    email: `${PREFIX}email`,
    firstName: `${PREFIX}first_name`,
    lastName: `${PREFIX}last_name`,
    address1: `${PREFIX}address1`,
    apartmentHouseNumber: `${PREFIX}apartment_house_number`,
    city: `${PREFIX}city`,
    state: `${PREFIX}state`,
    zipCode: `${PREFIX}zip_code`,
    phoneNumber: `${PREFIX}phone_number`,
    country: `${PREFIX}country`
}

/** Postal codes are named differently enough per market to warrant their own wording. */
export const ZIP_REQUIRED_MESSAGE_KEYS: Record<string, string> = {
    us: `${PREFIX}zip_code_us`,
    gb: `${PREFIX}zip_code_gb`,
    au: `${PREFIX}zip_code_au`
}

/** Format failures name the local spelling rather than a generic "invalid". */
export const ZIP_FORMAT_MESSAGE_KEYS: Record<string, string> = {
    us: `${PREFIX}us_zip_code_validation_message`,
    de: `${PREFIX}zip_code_letters_not_allowed`,
    at: `${PREFIX}zip_code_letters_not_allowed`
}

export interface FieldErrorMessageContext {
    langCode: string
    field?: CheckoutFieldName
    countryCode?: string
    /** Placeholder values, e.g. the country and calling code in the phone message. */
    params?: Record<string, string | number>
}

const requiredMessageKey = ({ field, countryCode }: FieldErrorMessageContext) => {
    if (!field) return FIELD_ERROR_MESSAGE_KEYS.value_missing
    if (field === "zipCode" && countryCode) {
        return (
            ZIP_REQUIRED_MESSAGE_KEYS[countryCode.toLowerCase()] ??
            REQUIRED_MESSAGE_KEYS.zipCode
        )
    }
    return REQUIRED_MESSAGE_KEYS[field]
}

/** The single place a code becomes words. */
export const resolveFieldErrorMessage = (
    code: FieldErrorCode,
    context: FieldErrorMessageContext
): string => {
    const key =
        code === "value_missing"
            ? requiredMessageKey(context)
            : code === "zip_code_format_invalid" && context.countryCode
              ? (ZIP_FORMAT_MESSAGE_KEYS[context.countryCode.toLowerCase()] ??
                FIELD_ERROR_MESSAGE_KEYS.zip_code_format_invalid)
              : FIELD_ERROR_MESSAGE_KEYS[code]
    return t(context.langCode, key, context.params)
}
